import React, { ReactNode } from 'react';
import { styled, Typography } from '@mui/material';

type ReportMetricCardTone = 'primary' | 'success' | 'warning' | 'info' | 'error';

type ReportMetricCardProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  tone?: ReportMetricCardTone;
};

const Card = styled('div')<{ tone: ReportMetricCardTone }>(({ theme, tone }) => {
  const color = theme.palette[tone].main;
  const contrast = theme.palette[tone].contrastText;

  return {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    minHeight: 112,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
    '& .report-metric-icon': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 44,
      height: 44,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: color,
      color: contrast,
    },
    '& .report-metric-value': {
      color,
      lineHeight: 1.1,
    },
  };
});

const Content = styled('div')({
  minWidth: 0,
});

const Helper = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  lineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const ReportMetricCard: React.FC<ReportMetricCardProps> = ({ icon, label, value, helper, tone = 'primary' }) => {
  return (
    <Card tone={tone}>
      <div className="report-metric-icon">{icon}</div>
      <Content>
        <Typography variant="caption" color="textSecondary" fontWeight={700}>
          {label}
        </Typography>
        <Typography className="report-metric-value" variant="h6" fontWeight={800}>
          {value}
        </Typography>
        {helper && (
          <Helper variant="caption" color="textSecondary">
            {helper}
          </Helper>
        )}
      </Content>
    </Card>
  );
};

export default ReportMetricCard;
